import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import type { ReactNode } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TurnFieldProposal } from "../../util/slopifyThread";
import { prettyLabel } from "../DynamicField";

interface FieldDiffRowProps {
  field: TurnFieldProposal;
  checked: boolean;
  onToggle: (fieldName: string, next: boolean) => void;
  manuallyDirtied: boolean;
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value.length > 0 ? value : "—";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

interface PaneProps {
  label: string;
  /** Plain-text content (used for scalar fields). Ignored when `children` is set. */
  text?: string;
  /** Structured content (used by the rich quizz previews). Takes precedence over `text`. */
  children?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  variant: "before" | "after";
}

/**
 * One BEFORE (gray) / AFTER (green) pane with the shared expand/collapse + fade
 * affordance. Accepts either plain `text` (scalar field diffs) or structured
 * `children` (the rich quizz previews) so both render with identical chrome.
 */
export const Pane = ({ label, text, children, expanded, onToggle, variant }: PaneProps) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const hasChildren = children !== undefined && children !== null;

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight);
  }, [text, children, expanded]);

  const needsToggle = overflows || expanded;
  const isBefore = variant === "before";
  const paneBg = isBefore ? "bg-gray-50" : "bg-green-50";
  const fadeFrom = isBefore ? "from-gray-50" : "from-green-50";
  const labelColor = isBefore ? "text-gray-400" : "text-green-400";
  const textColor = isBefore ? "text-gray-500" : "text-gray-800";
  const sizeClass = expanded ? "max-h-64 overflow-auto" : "max-h-20 overflow-hidden";

  return (
    <div className={`relative rounded p-1 ${paneBg}`} title={hasChildren ? undefined : text}>
      <div className={`text-[9px] uppercase tracking-wide ${labelColor} mb-0.5`}>{label}</div>
      <div className="relative">
        <div
          ref={contentRef}
          className={`text-[10px] whitespace-pre-wrap break-words ${textColor} ${sizeClass}`}
        >
          {hasChildren ? children : text}
        </div>
        {!expanded && overflows && (
          <div
            aria-hidden="true"
            className={`absolute inset-x-0 bottom-0 h-5 bg-gradient-to-t ${fadeFrom} to-transparent pointer-events-none`}
          />
        )}
      </div>
      {needsToggle && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="mt-1 inline-flex items-center gap-0.5 text-[9px] text-gray-500 hover:text-gray-700"
        >
          {expanded ? (
            <>
              <ChevronUpIcon className="w-2.5 h-2.5" />
              {t("general.assistantScenarIa.collapse")}
            </>
          ) : (
            <>
              <ChevronDownIcon className="w-2.5 h-2.5" />
              {t("general.assistantScenarIa.expand")}
            </>
          )}
        </button>
      )}
    </div>
  );
};

export const FieldDiffRow = ({ field, checked, onToggle, manuallyDirtied }: FieldDiffRowProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<{ before: boolean; after: boolean }>({
    before: false,
    after: false,
  });
  const label = prettyLabel(field.fieldName, t as any);
  const beforeText = formatValue(field.before);
  const afterText = formatValue(field.after);

  const baseClass = field.unchanged
    ? "opacity-60 bg-gray-50 border-gray-100"
    : "bg-white border-gray-200";

  return (
    <div className={`rounded-md border p-2 mb-1.5 ${baseClass}`}>
      <div className="flex items-center gap-2 mb-1">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-green-500 disabled:opacity-50"
          checked={checked}
          disabled={field.unchanged}
          onChange={(e) => onToggle(field.fieldName, e.target.checked)}
          aria-label={label}
        />
        <span className="text-[11px] font-medium text-gray-700 truncate flex-1" title={label}>
          {label}
        </span>
        {field.unchanged && (
          <span className="text-[10px] italic text-gray-400">
            {t("general.assistantScenarIa.unchanged")}
          </span>
        )}
        {manuallyDirtied && !field.unchanged && (
          <span
            className="text-[10px] text-amber-600 whitespace-nowrap"
            title={t("general.assistantScenarIa.manuallyDirtiedTooltip") as string}
          >
            {t("general.assistantScenarIa.manuallyDirtiedBadge")}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Pane
          label={t("general.assistantScenarIa.diffBefore") as string}
          text={beforeText}
          expanded={expanded.before}
          onToggle={() => setExpanded((prev) => ({ ...prev, before: !prev.before }))}
          variant="before"
        />
        <Pane
          label={t("general.assistantScenarIa.diffAfter") as string}
          text={afterText}
          expanded={expanded.after}
          onToggle={() => setExpanded((prev) => ({ ...prev, after: !prev.after }))}
          variant="after"
        />
      </div>
    </div>
  );
};

export default FieldDiffRow;
